import React, { useEffect, useContext, useState, useRef } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { useRouteMatch, useLocation } from 'react-router-dom'
import objectPath from 'object-path'
import WidgetLayout from 'designer/component/display/layout/WidgetLayout'

// Action
import {
  getWidget,
  loadUrlParamToFormData,
  resetJsonForm,
} from 'designer/component/action/Design'
import { widgetSchema, widgetUischema } from 'designer/component/action/Schema'
import FormLoading from 'components/Loader/FormLoading'
import { getElementAction } from 'designer/component/action/Condition'
import { debounceCallback } from 'utility/debounceCallback.util'
import { onCheckCondition } from 'designer/component/action/condition/Action'
import {
  FORM_PAGE_READY_EVENT,
  getFormPageReady,
  getFormPageReadyKey,
} from 'designer/component/action/FormPageReady'

const WIDGET_HEADER_READY_DELAY = 500
const WIDGET_HEADER_READY_MAX_DELAY = 5000
const WIDGET_HEADER_FORM_PAGE_WAIT_DELAY = 1000

const WidgetHeader = (prop) => {
  const { widgetCode, designerData = {}, designerConfig = {} } = prop

  const match = useRouteMatch()
  const location = useLocation()

  let search = location.search || []
  let pathParams = match.params
  let urlSearchParams = new URLSearchParams(search)
  let queryParams = Object.fromEntries([...urlSearchParams])
  let perspectiveRoute = match.params.perspective || ''
  const urlParams = { ...pathParams, ...queryParams }
  const widgetHeaderReadyKey = `${widgetCode}:${location.pathname}:${location.search}`
  const formPageReadyKey = getFormPageReadyKey(location)

  const designContext = useContext(DesignContext)
  const { jsonForm, resetContext, setFormData, setDatasourceValue } =
    designContext
  let datasourceValue = objectPath.get(designContext, '@datasource') || {}
  const [isLoadPage, setIsLoadPage] = useState(true)
  const [isPageReady, setIsPageReady] = useState(false)
  const [pageReadyKey, setPageReadyKey] = useState('')
  const pageReadyTimerRef = useRef(null)
  const pageReadyMaxTimerRef = useRef(null)
  const pageResizeObserverRef = useRef(null)

  useEffect(async () => {
    setIsLoadPage(true)
    if (!widgetCode) return
    let datasourceValue = {}
    let { jsonForm, formDesign, datasource } = await getWidget({
      widgetCode: widgetCode,
      pathParams: pathParams,
      queryParams: queryParams,
      location: location,
      useFormData: true,
      urlParams: urlParams,
      designerData: designerData,
      datasourceValue: datasourceValue,
    })
    let conditions = objectPath.get(jsonForm, 'conditions')
    let formData = objectPath.get(jsonForm, 'formData')
    let elementAction = getElementAction(
      designContext,
      conditions,
      jsonForm,
      formData
    )
    resetJsonForm({
      designCode: widgetCode,
      formDesign: formDesign,
      jsonForm: jsonForm,
      perspectiveRoute: perspectiveRoute,
      resetContext: resetContext,
      uischema: widgetUischema,
      schema: widgetSchema,
      urlParams: urlParams,
      elementAction: elementAction,
      datasource: datasource,
    })
    setIsLoadPage(false)
  }, [widgetCode])

  useEffect(() => {
    if (designContext?.formDesign?.formDesignId) {
      let formData = objectPath.get(jsonForm, 'formData')
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue
      )
      objectPath.set(datasourceValue, 'queryParam', queryParams)
      objectPath.set(designContext, '@datasource', datasourceValue)
      objectPath.set(designContext, 'jsonForm.formData', formData)
      setDatasourceValue(datasourceValue)
      setFormData(formData)
      debounceCallback(onCheckCondition, [designContext], 'check-widget')
    }
  }, [designContext?.formDesign?.formDesignId, JSON.stringify(queryParams)])

  useEffect(() => {
    let isPageReadyStarted = false
    let formPageWaitTimer = null

    const clearFormPageWait = () => {
      if (formPageWaitTimer) {
        window.clearTimeout(formPageWaitTimer)
        formPageWaitTimer = null
      }
    }
    const clearPageReadyWait = () => {
      if (pageReadyTimerRef.current) {
        window.clearTimeout(pageReadyTimerRef.current)
        pageReadyTimerRef.current = null
      }

      if (pageReadyMaxTimerRef.current) {
        window.clearTimeout(pageReadyMaxTimerRef.current)
        pageReadyMaxTimerRef.current = null
      }

      if (pageResizeObserverRef.current) {
        pageResizeObserverRef.current.disconnect()
        pageResizeObserverRef.current = null
      }
    }
    const finishPageReady = () => {
      clearPageReadyWait()
      setPageReadyKey(widgetHeaderReadyKey)
      setIsPageReady(true)
    }
    const waitPageReady = () => {
      clearPageReadyWait()
      pageReadyTimerRef.current = window.setTimeout(
        finishPageReady,
        WIDGET_HEADER_READY_DELAY,
      )
      pageReadyMaxTimerRef.current = window.setTimeout(
        finishPageReady,
        WIDGET_HEADER_READY_MAX_DELAY,
      )

      if (window.ResizeObserver) {
        let pageContainer =
          document.querySelector('.pcoded-main-container') || document.body

        pageResizeObserverRef.current = new window.ResizeObserver(() => {
          if (pageReadyTimerRef.current) {
            window.clearTimeout(pageReadyTimerRef.current)
          }

          pageReadyTimerRef.current = window.setTimeout(
            finishPageReady,
            WIDGET_HEADER_READY_DELAY,
          )
        })
        pageResizeObserverRef.current.observe(pageContainer)
      }
    }
    const handleLoad = () => {
      waitPageReady()
    }
    const startPageReadyWait = () => {
      if (isPageReadyStarted) return

      isPageReadyStarted = true

      if (document.readyState === 'complete') {
        waitPageReady()
      } else {
        window.addEventListener('load', handleLoad)
      }
    }
    const handleFormPageReady = ({ detail = {} }) => {
      if (detail.readyKey !== formPageReadyKey) return

      if (detail.isReady) {
        clearFormPageWait()
        startPageReadyWait()
        return
      }

      if (detail.isLoading) {
        clearFormPageWait()
      }
    }

    setIsPageReady(false)
    setPageReadyKey('')

    let formPageReady = getFormPageReady(formPageReadyKey)
    window.addEventListener(FORM_PAGE_READY_EVENT, handleFormPageReady)

    if (formPageReady.isReady) {
      startPageReadyWait()
    } else if (!formPageReady.isLoading) {
      formPageWaitTimer = window.setTimeout(
        startPageReadyWait,
        WIDGET_HEADER_FORM_PAGE_WAIT_DELAY,
      )
    }

    return () => {
      clearFormPageWait()
      window.removeEventListener(FORM_PAGE_READY_EVENT, handleFormPageReady)
      window.removeEventListener('load', handleLoad)
      clearPageReadyWait()
    }
  }, [widgetHeaderReadyKey, formPageReadyKey])

  if (!isPageReady || pageReadyKey !== widgetHeaderReadyKey) {
    return false
  }

  if (isLoadPage) {
    return <FormLoading height={80} />
  }

  return (
    <WidgetLayout
      schema={jsonForm.schema}
      uischema={jsonForm.uischema}
      designerConfig={designerConfig}
    />
  )
}

export default WidgetHeader
